/**
 * Privacy-safe logger. Never log raw API responses, profile/session data,
 * tokens, authorization codes, state, or full emails (spec §17.1, §23).
 *
 * Telemetry event names and attributes are restricted to the allow-lists in
 * spec §23. This is intentionally a thin sink: wire it to a real telemetry
 * vendor later (CloudWatch RUM/Sentry) without changing call sites.
 */
import { env } from './env';

export type TelemetryEvent =
  | 'auth_portal_viewed'
  | 'auth_provider_registry_loaded'
  | 'auth_login_started'
  | 'auth_complete_loaded'
  | 'auth_session_resolved'
  | 'auth_onboarding_submitted'
  | 'auth_identity_link_started'
  | 'auth_identity_unlink_succeeded'
  | 'auth_identity_unlink_failed'
  | 'auth_logout_started'
  | 'auth_logout_all_succeeded'
  | 'auth_error_presented';

/** Allow-listed, non-PII attributes only (spec §23). */
export interface TelemetryAttributes {
  provider?: string;
  client?: string;
  route?: string;
  errorCode?: string;
  httpStatus?: number;
  appVersion?: string;
  browserFamily?: string;
}

export function track(event: TelemetryEvent, attributes: TelemetryAttributes = {}): void {
  if (!env.internalDebug) return;
  // Debug-only sink. A production telemetry adapter would forward the allow-listed
  // event + attributes; it must never receive email/userId/token/state/etc.
  console.warn(`[telemetry] ${event}`, { appVersion: env.appVersion, ...attributes });
}

/** Diagnostic warning for developers; gated behind internalDebug to avoid noise. */
export function debugWarn(message: string): void {
  if (!env.internalDebug) return;
  console.warn(`[portal] ${message}`);
}
